<template>
  <div>
    <div
      class="body-1-bold inline-block"
      :class="{
        'text-static-default-hi-disabled': isOutOfStock,
      }"
    >
      <template v-if="isCartLocked || !link">
        {{ title }}
      </template>

      <RevLink
        v-else
        class="inline-block"
        :class="{
          '!text-static-default-hi-disabled': isOutOfStock,
        }"
        target="_blank"
        :to="link"
        :underlined="false"
      >
        {{ title }}
      </RevLink>
    </div>

    <ul class="my-4 flex flex-wrap gap-4">
      <li v-for="tag in tags" :key="tag" class="max-h-[20px]">
        <RevTag
          :class="{
            'text-static-default-hi-disabled': isOutOfStock,
          }"
          :label="tag"
          variant="secondary"
          variation="outline"
        />
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject } from 'vue'

import type { LinkInternal } from '@backmarket/http-api/src/api-models/LinkInternal'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevLink } from '@ds/components/Link'
import { RevTag } from '@ds/components/Tag'

import {
  BatteryType,
  getBatteryType,
} from '~/scopes/checkout/composables/useBatteryType'
import { useBatteryConfidenceExperiment } from '~/scopes/product-core/composables/useBatteryConfidenceExperiment/useBatteryConfidenceExperiment'
import {
  type BatteryConfidenceTier,
  useBatteryConfidenceTier,
} from '~/scopes/product-core/composables/useBatteryConfidenceTier/useBatteryConfidenceTier'

import translations from './Description.translations'

const SMARTPHONE_CATEGORY_ID = 2

const BATTERY_TIER_TAGS: Record<
  BatteryConfidenceTier,
  {
    B: { id: string; defaultMessage: string }
    C: { id: string; defaultMessage: string }
  }
> = {
  NEW: {
    B: translations.battery_new_variant_b,
    C: translations.battery_new_variant_c,
  },
  NINETY_NINE: {
    B: translations.battery_90_99_variant_b,
    C: translations.battery_90_99_variant_c,
  },
  STANDARD: {
    B: translations.battery_standard_variant_b,
    C: translations.battery_standard_variant_c,
  },
}

const props = defineProps<{
  title: string
  link?: string | null | LinkInternal
  gradeId: number
  gradeName: string | null
  categoryId: number
  specialOfferType: number
  simType: string | null
  isOutOfStock: boolean
}>()

const BATTERY_TAGS = {
  [BatteryType.BATTERY_NEW]: translations.battery_new,
  [BatteryType.BATTERY_90_99]: translations.battery_90_99,
  [BatteryType.BATTERY_STANDARD]: translations.battery_standard,
}

const isCartLocked = inject('isCartLocked', false)

const i18n = useI18n()
const { isControl, isWithText } = useBatteryConfidenceExperiment()
const { getBatteryConfidenceTier } = useBatteryConfidenceTier()

const isSmartphone = computed(() => props.categoryId === SMARTPHONE_CATEGORY_ID)

const tags = computed(() => {
  const items = []

  if (props.specialOfferType === 10) {
    items.push(i18n(translations.outlet_offer_type))
  } else {
    items.push(props.gradeName)
  }

  if (isControl.value || !isSmartphone.value) {
    const batteryType = getBatteryType(
      props.gradeId,
      props.specialOfferType,
      props.categoryId,
    )
    const batteryTag = batteryType ? BATTERY_TAGS[batteryType] : null
    if (batteryTag) {
      items.push(i18n(batteryTag))
    }
  } else {
    const tier = getBatteryConfidenceTier({
      specialOfferType: props.specialOfferType,
      gradeId: props.gradeId,
    })
    if (tier) {
      const variant = isWithText.value ? 'B' : 'C'
      items.push(i18n(BATTERY_TIER_TAGS[tier][variant]))
    }
  }

  if (props.simType) {
    items.push(props.simType)
  }

  return items.filter(Boolean) as Array<string>
})
</script>
